import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";

export type TagValidationResult = "duplicate" | "invalid" | null;

export interface UseTagInputOptions {
  value: string[];
  onChange: (tags: string[]) => void;
  validateTag?: (tag: string, currentTags: string[]) => TagValidationResult;
  transformOnAdd?: (tag: string) => string | null;
  onValidationError?: (result: TagValidationResult, tag: string) => void;
  inputFilter?: (value: string) => boolean;
}

const DEFAULT_INPUT_FILTER = (value: string) => !value.includes(" ");

export function useTagInput({
  value,
  onChange,
  validateTag,
  transformOnAdd,
  onValidationError,
  inputFilter,
}: UseTagInputOptions) {
  const [inputValue, setInputValue] = useState("");
  const valueRef = useRef(value);
  valueRef.current = value;

  const addTag = useCallback(
    (rawTag: string) => {
      const trimmedTag = rawTag.trim();
      if (!trimmedTag) {
        return;
      }

      const transformedTag = transformOnAdd
        ? transformOnAdd(trimmedTag)
        : trimmedTag;

      if (transformedTag === null) {
        return;
      }

      const currentValue = valueRef.current;
      const validationResult = validateTag
        ? validateTag(trimmedTag, currentValue)
        : null;

      if (validationResult) {
        onValidationError?.(validationResult, trimmedTag);
        return;
      }

      onChange([...new Set([...currentValue, transformedTag])]);
      setInputValue("");
    },
    [onChange, onValidationError, transformOnAdd, validateTag],
  );

  const removeTag = useCallback(
    (index: number) => {
      const currentValue = valueRef.current;
      const nextTags = [...currentValue];
      nextTags.splice(index, 1);
      onChange(nextTags);
    },
    [onChange],
  );

  const removeLastTag = useCallback(() => {
    const currentValue = valueRef.current;
    if (currentValue.length > 0) {
      removeTag(currentValue.length - 1);
    }
  }, [removeTag]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextValue = event.target.value;
      const filter = inputFilter ?? DEFAULT_INPUT_FILTER;

      if (!filter(nextValue)) {
        return;
      }

      setInputValue(nextValue);
    },
    [inputFilter],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.repeat) {
        return;
      }

      if (event.nativeEvent.isComposing || event.keyCode === 229) {
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        addTag(inputValue);
        return;
      }

      if (event.key === "Backspace" && inputValue === "") {
        event.preventDefault();
        removeLastTag();
        return;
      }

      if (event.key === " ") {
        const filter = inputFilter ?? DEFAULT_INPUT_FILTER;
        if (!filter(" ")) {
          event.preventDefault();
        }
      }
    },
    [addTag, inputFilter, inputValue, removeLastTag],
  );

  return {
    inputValue,
    setInputValue,
    addTag,
    removeTag,
    removeLastTag,
    handleInputChange,
    handleKeyDown,
  };
}
